import {
  Check,
  ContentCopy,
  ExitToApp,
  Fingerprint,
  ManageAccounts
} from "@mui/icons-material";
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  ClickAwayListener,
  Fade,
  IconButton,
  Popper,
  Tooltip,
  Typography
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTenant } from "features/shell";
import { MemberAvatar } from "features/solution-member";
import { useUserProfile } from "features/user-profile";
import { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { createRoute } from "utils/url";

const useStyles = makeStyles(theme => ({
  paddingLeft: { paddingLeft: theme.spacing(2) },
  popper: {
    top: "10px !important",
    right: 8,
    zIndex: 1000,
    left: "unset !important"
  },
  cursor: { cursor: "pointer" },
  logoutIcon: { color: "#ffb900" },
  permissionsIcon: { color: "#ffb900" },
  rolesContent: {
    paddingTop: 0,
    paddingBottom: theme.spacing(1)
  },
  rolesLabel: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: 11,
    color: "#1E1E1E",
    marginBottom: theme.spacing(0.5)
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5)
  },
  roleChip: {
    backgroundColor: "#3AA9AE",
    color: "#ffffff",
    height: 20,
    "& .MuiChip-label": {
      fontFamily: "Segoe UI",
      fontSize: "10px",
      fontWeight: 600,
      paddingLeft: 8,
      paddingRight: 8
    }
  },
  ssoRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    opacity: 0.65
  },
  ssoIcon: {
    fontSize: "11px !important"
  },
  ssoText: {
    fontFamily: "Segoe UI",
    fontSize: 11,
    maxWidth: 140,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const
  },
  copyButton: {
    padding: 2
  }
}));

export type UserInfo = {
  name: string;
  familyName: string;
  email: string;
  userName: string;
  signOut: () => void;
};

export default function UserGreeting(props: UserInfo) {
  const {
    paddingLeft,
    popper,
    cursor,
    logoutIcon,
    permissionsIcon,
    rolesContent,
    rolesLabel,
    chipRow,
    roleChip,
    ssoRow,
    ssoIcon,
    ssoText,
    copyButton
  } = useStyles({});

  const {
    name: firstName,
    familyName: lastName,
    email,
    userName,
    signOut
  } = props;
  const user = { firstName, lastName };
  const { tenantKey } = useTenant();
  const { data: profileData } = useUserProfile();
  const { push } = useHistory();

  const roles = profileData?.roles ?? [];

  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div className={paddingLeft}>
        <div onClick={handleToggle} className={cursor} ref={anchorRef}>
          <MemberAvatar large dataTest="logged-in-user" member={user} />
        </div>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          className={popper}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Card>
                <CardHeader
                  avatar={<MemberAvatar aria-label="recipe" member={user} />}
                  action={
                    <>
                      <Tooltip title="My profile &amp; permissions">
                        <IconButton
                          data-testid="user-profile-button"
                          aria-label="my profile"
                          onClick={() => {
                            push(createRoute("/user-profile"));
                            setOpen(false);
                          }}
                          size="large"
                        >
                          <ManageAccounts className={permissionsIcon} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sign Out">
                        <IconButton
                          data-test="sign-out-button"
                          aria-label="settings"
                          onClick={signOut}
                          size="large"
                        >
                          <ExitToApp className={logoutIcon} />
                        </IconButton>
                      </Tooltip>
                    </>
                  }
                  title={`${firstName} ${lastName}`}
                  subheader={
                    <>
                      {email}
                      {userName !== email && (
                        <>
                          <br />
                          <span className={ssoRow}>
                            <Fingerprint className={ssoIcon} />
                            <Tooltip title={userName}>
                              <span className={ssoText}>
                                {userName.length > 22
                                  ? `${userName.slice(0, 18)}\u2026`
                                  : userName}
                              </span>
                            </Tooltip>
                            <Tooltip title={copied ? "Copied!" : "Copy SSO ID"}>
                              <IconButton
                                size="small"
                                className={copyButton}
                                onClick={e => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(userName);
                                  setCopied(true);
                                  setTimeout(() => setCopied(false), 2000);
                                }}
                              >
                                {copied ? (
                                  <Check className={ssoIcon} />
                                ) : (
                                  <ContentCopy className={ssoIcon} />
                                )}
                              </IconButton>
                            </Tooltip>
                          </span>
                        </>
                      )}
                      <br />
                      Connected to tenant: {tenantKey}
                    </>
                  }
                />
                {roles.length > 0 && (
                  <CardContent className={rolesContent}>
                    <Typography className={rolesLabel}>Roles</Typography>
                    <div className={chipRow}>
                      {roles.map(role => (
                        <Chip
                          key={role}
                          label={role}
                          className={roleChip}
                          data-testid={`role-${role}`}
                        />
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            </Fade>
          )}
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
